import { forgetDevice, rememberDevice } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

import { getRedirectAfterSignIn } from "@/auth/utils/getRedirectAfterSignIn";
import { rememberDeviceLocalStorageKey } from "@/common/hooks/useRememberDeviceState";
import { useLogger } from "@/logging/client/LoggerContext";
import { assignLocation } from "@/setup/frontend/assignLocation";
import { useEffect } from "react";

type Logger = ReturnType<typeof useLogger>;

export function AmplifyAuthListener() {
	const logger = useLogger();
	useEffect(() => {
		const stopListening = Hub.listen("auth", async ({ payload }) => {
			if (payload.event === "signedIn") {
				logger.info("AmplifyAuthListener: signedIn");
				await completeSignIn(logger);
			} else if (payload.event === "signedOut") {
				logger.info(`AmplifyAuthListener: signedOut ${payload.message}`);
			} else if (payload.event === "tokenRefresh") {
				logger.info(`AmplifyAuthListener: tokenRefresh ${payload.message}`);
			} else if (payload.event === "tokenRefresh_failure") {
				logger.error(
					`AmplifyAuthListener: tokenRefresh_failure ${payload.data.error?.name} ${payload.data.error?.message} ${payload.data.error?.recoverySuggestion}`,
				);
			}
		});

		return () => {
			stopListening();
		};
	}, []);

	return null;
}

export async function completeSignIn(logger: Logger): Promise<void> {
	await syncRememberedDevice(logger);
	redirectAfterSignIn(logger);
}

async function syncRememberedDevice(logger: Logger): Promise<void> {
	const rememberDeviceState =
		localStorage.getItem(rememberDeviceLocalStorageKey) === "true";
	if (rememberDeviceState) {
		await rememberDevice().catch((err) => {
			logger.error(`AmplifyAuthListener: rememberDevice - error - ${err}`);
		});
	} else {
		await forgetDevice().catch((err) => {
			logger.error(`AmplifyAuthListener: forgetDevice - error - ${err}`);
		});
	}
}

function redirectAfterSignIn(logger: Logger): void {
	const searchParams = new URLSearchParams(window.location.search);
	const redirectPath = getRedirectAfterSignIn({
		redirectParam: searchParams.get("redirect"),
		origin: window.location.origin,
	});

	logger.info(`AmplifyAuthListener: signedIn redirect to ${redirectPath}`);
	assignLocation(redirectPath);
}
